import React, { useContext, useState, useEffect } from 'react';
import Api from 'api';
import { storage, observer } from 'store';
import { FormGrid, FormField, FormRow, FormColumn, FormSubmit } from 'components/forms';
import { Input } from 'components/UI';
import withForm from 'components/forms/withForm';
import { ErrorsShakeHubContext, findFirstFieldWithError } from 'components/forms/utils';
import { corporateFormSchema } from 'components/forms/validationSchemas';
import { pushCustomSubmitEvent } from 'utils';
import { getCountryCode } from 'utils/localised';
import { phoneCode } from 'define/locale';

import * as styles from './CorporateForm.module.scss';

const Form = ({
  handleSubmit,
  fields,
  isValid,
  values,
  errors,
  isSubmitting,
  status,
  setStatus,
  formName,
  id,
  className,
  AdditionalInformationForForm,
}) => {
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const [btnPressed, setBtnPressed] = useState(false);

  useEffect(() => {
    return () => storage.addItem('isEmailCheckedSuccessful', false);
  }, []);

  useEffect(() => {
    if (!isValid && btnPressed) {
      errorsShakeHub.dispatchEvent(new CustomEvent('shakeError'));
      errorsShakeHub.dispatchEvent(
        new CustomEvent('setFocus', {
          detail: findFirstFieldWithError(values, errors),
        }),
      );
      setBtnPressed(false);
    }
  }, [isValid, btnPressed]);

  useEffect(() => {
    if (status === 'success') {
      setStatus();
    }
  }, [status]);

  return (
    <FormGrid
      submitHandler={e => {
        setBtnPressed(true);
        handleSubmit(e);
      }}
      formName={formName}
      id={id}
      className={className}
    >
      <FormRow sizes={['sm12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.name} />
          <FormField
            Component={Input}
            {...fields.email}
            isLoading={storage.getItem('isEmailCheckLoading')}
            isChecked={storage.getItem('isEmailCheckedSuccessful')}
          />
          <FormField Component={Input} {...fields.phone} />
          <FormField Component={Input} {...fields.message} />
        </FormColumn>
      </FormRow>
      {AdditionalInformationForForm}
      <div className={styles.submitWrap}>
        <FormSubmit
          title={'Register'}
          nda={fields.nda}
          photoConsent={fields.photoConsent}
          disabled={isSubmitting}
          size="sm"
        />
      </div>
    </FormGrid>
  );
};

export default function CorporateForm({
  id = 'hamburg-ai-and-china-in-action',
  formName = 'hamburg-ai-and-china-in-action',
  closeModal,
  onSuccess,
  additionalFormData,
  className,
  getAdditionalFormData,
  AdditionalInformationForForm = '',
  inModal = false,
  withNda = true,
  withPhotoConsent = true,
}) {
  const fields = {
    name: {
      initialValue: '',
      domProps: {
        placeholder: 'Name',
        maxLength: 255,
      },
    },
    email: {
      initialValue: '',
      inModal,
      domProps: {
        placeholder: 'Corporate email',
        type: 'email',
        maxLength: 254,
      },
    },
    phone: {
      initialValue: '',
      getInitialValue,
      mask: {
        mask: '+99999999999999999',
        maskChar: '',
        formatChars: {
          9: '[0-9]',
        },
      },
      domProps: {
        placeholder: 'Phone number',
        type: 'tel',
      },
    },
    message: {
      initialValue: '',
      domProps: {
        maxLength: 500,
        placeholder: 'Position',
      },
    },
  };

  if (withNda) {
    fields.nda = {
      initialValue: false,
      inModal,
      withoutError: true,
      content: 'I agree to sign an NDA to further protect my data.',
    };
  }

  if (withPhotoConsent) {
    fields.photoConsent = {
      initialValue: false,
      inModal,
      withoutError: true,
      content:
        'I consent to being photographed, and for these images to be used in media if necessary.',
    };
  }

  const errorsMessages = {
    name: 'Please enter your full name',
    email: {
      required: 'Please provide your corporate email address',
      notCorporateEmail: 'Please provide your corporate email address',
    },
    phone: 'Please provide your phone number',
    message: 'Please specify your job title',
  };

  const defaultOnSuccess = () => storage.addItem('isEmailCheckedSuccessful', false);

  const Component = withForm({
    fields,
    validation: props => corporateFormSchema(errorsMessages, props),
    submit: {
      id,
      formName,
      handler: Api.sendForm.bind(Api, 'integration/form-data'),
      reset: true,
      successModal: 'SuccessModal',
      errorModal: 'ErrorModal',
      onSuccess: onSuccess ?? defaultOnSuccess,
      additionalFormData,
      getAdditionalFormData,
      pushSubmitEvent: () => pushCustomSubmitEvent(id),
      onResponse: () => {
        if (inModal) {
          closeModal();
        }
      },
    },
    formName,
    id,
    className,
    AdditionalInformationForForm,
  })(observer(Form));

  return <Component />;
}

function getInitialValue() {
  if (typeof window !== 'undefined') {
    const country = getCountryCode()?.toLowerCase();
    return phoneCode[country] ?? '+';
  }
  return '+';
}
